"use client";

import { differenceInSeconds } from "date-fns";
import { enUS, fi } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";

export type CountdownAccent = "amber" | "sky" | "emerald";

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeRemaining(targetDate: Date): TimeRemaining {
  const now = new Date();
  const totalSeconds = Math.max(0, differenceInSeconds(targetDate, now));

  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
}

/**
 * Full literal Tailwind class strings per accent. Kept literal (not
 * concatenated) so Tailwind's content scanner can detect every utility.
 */
const ACCENT_CLASSES: Record<
  CountdownAccent,
  {
    card: string;
    blurLarge: string;
    blurSmall: string;
    iconWrap: string;
    title: string;
    timer: string;
    placeholder: string;
  }
> = {
  amber: {
    card: "border-amber-500/20 from-amber-500/5 via-amber-500/10 to-amber-600/5",
    blurLarge: "bg-amber-500/10",
    blurSmall: "bg-amber-500/5",
    iconWrap: "bg-amber-500/20",
    title: "text-amber-400/80",
    timer: "text-amber-400",
    placeholder: "bg-amber-400/20",
  },
  sky: {
    card: "border-sky-500/20 from-sky-500/5 via-sky-500/10 to-sky-600/5",
    blurLarge: "bg-sky-500/10",
    blurSmall: "bg-sky-500/5",
    iconWrap: "bg-sky-500/20",
    title: "text-sky-400/80",
    timer: "text-sky-400",
    placeholder: "bg-sky-400/20",
  },
  emerald: {
    card: "border-emerald-500/20 from-emerald-500/5 via-emerald-500/10 to-emerald-600/5",
    blurLarge: "bg-emerald-500/10",
    blurSmall: "bg-emerald-500/5",
    iconWrap: "bg-emerald-500/20",
    title: "text-emerald-400/80",
    timer: "text-emerald-400",
    placeholder: "bg-emerald-400/20",
  },
};

interface CountdownTimerProps {
  timeRemaining: TimeRemaining;
  colorClass: string;
}

function CountdownTimer({ timeRemaining, colorClass }: CountdownTimerProps) {
  const t = useTranslations();
  const { days, hours, minutes, seconds } = timeRemaining;

  // Only show a unit if it or any larger unit is non-zero
  const showDays = days > 0;
  const showHours = showDays || hours > 0;
  const showMinutes = showHours || minutes > 0;

  return (
    <div className="flex items-baseline justify-center gap-1 flex-wrap">
      {showDays && (
        <div className="flex items-baseline gap-1">
          <span
            className={`font-cinzel text-2xl sm:text-3xl font-bold ${colorClass}`}
          >
            {days}
          </span>
          <span className={`font-spectral text-xs sm:text-sm ${colorClass}/70`}>
            {t("tournaments.countdown.days")}
          </span>
        </div>
      )}

      {showHours && (
        <div className="flex items-baseline gap-1">
          <span
            className={`font-cinzel text-2xl sm:text-3xl font-bold ${colorClass}`}
          >
            {hours.toString().padStart(2, "0")}
          </span>
          <span className={`font-spectral text-xs sm:text-sm ${colorClass}/70`}>
            {t("tournaments.countdown.hours")}
          </span>
        </div>
      )}

      {showMinutes && (
        <div className="flex items-baseline gap-1">
          <span
            className={`font-cinzel text-2xl sm:text-3xl font-bold ${colorClass}`}
          >
            {minutes.toString().padStart(2, "0")}
          </span>
          <span className={`font-spectral text-xs sm:text-sm ${colorClass}/70`}>
            {t("tournaments.countdown.minutes")}
          </span>
        </div>
      )}

      {/* Seconds - always shown */}
      <div className="flex items-baseline gap-1">
        <span
          className={`font-cinzel text-2xl sm:text-3xl font-bold ${colorClass}`}
        >
          {seconds.toString().padStart(2, "0")}
        </span>
        <span className={`font-spectral text-xs sm:text-sm ${colorClass}/70`}>
          {t("tournaments.countdown.seconds")}
        </span>
      </div>
    </div>
  );
}

function CountdownPlaceholder({ colorClass }: { colorClass: string }) {
  return (
    <div className="flex items-baseline justify-center gap-1 flex-wrap">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex items-baseline gap-1">
          <div
            className={`h-8 w-10 sm:h-9 sm:w-12 rounded ${colorClass} animate-pulse`}
          />
        </div>
      ))}
    </div>
  );
}

interface CountdownCardProps {
  /** Date the countdown ticks down to. */
  targetDate: Date;
  /** Emoji rendered in the icon circle. */
  icon: string;
  /** Already-translated heading text. */
  title: string;
  /** Accent colour theme. */
  accent: CountdownAccent;
}

/**
 * A single self-contained countdown card with a live ticking timer. Used both
 * on the sign-in phase (TournamentCountdown) and above the tower while it is
 * visible but the tournament has not yet started.
 */
export function CountdownCard({
  targetDate,
  icon,
  title,
  accent,
}: CountdownCardProps) {
  const locale = useLocale();
  const dateLocale = locale === "fi" ? fi : enUS;
  const atWord = locale === "fi" ? "klo" : "at";
  const classes = ACCENT_CLASSES[accent];

  // Initialize with null to avoid SSR/client time mismatch
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(
    null,
  );

  useEffect(() => {
    const tick = () => setTimeRemaining(getTimeRemaining(targetDate));

    tick();
    const interval = setInterval(tick, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br p-6 ${classes.card}`}
    >
      {/* Background decoration */}
      <div
        className={`absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl ${classes.blurLarge}`}
      />
      <div
        className={`absolute -bottom-4 -left-4 h-16 w-16 rounded-full blur-xl ${classes.blurSmall}`}
      />

      <div className="relative flex flex-col items-center text-center">
        {/* Icon */}
        <div
          className={`mb-3 flex h-12 w-12 items-center justify-center rounded-full ${classes.iconWrap}`}
        >
          <span className="text-2xl">{icon}</span>
        </div>

        {/* Title */}
        <h3
          className={`mb-1 font-cinzel text-sm font-semibold uppercase tracking-wider ${classes.title}`}
        >
          {title}
        </h3>

        {/* Date */}
        <p className="mb-3 font-spectral text-lg text-white/90">
          {formatInTimeZone(
            targetDate,
            "Europe/Helsinki",
            `d. MMMM yyyy '${atWord}' HH:mm`,
            { locale: dateLocale },
          )}
        </p>

        {/* Countdown */}
        {timeRemaining ? (
          <CountdownTimer
            timeRemaining={timeRemaining}
            colorClass={classes.timer}
          />
        ) : (
          <CountdownPlaceholder colorClass={classes.placeholder} />
        )}
      </div>
    </div>
  );
}
